// =============================================================================
// NotesToHankAdapter.ts — Browser WebSocket adapter for Notes-to-Hank
// =============================================================================

import { LOG } from '../../common/Utilities';
import type { NoteMessage, NoteConnectionState } from './NotesToHankTypes';
import { parseInboundMessage } from './notesToHankMessageUtils';

const LOG_PREFIX = '[NTH-Adapter]';

// -----------------------------------------------------------------------------
// Config
// -----------------------------------------------------------------------------

export interface NotesToHankAdapterConfig {
  url: string;
  getAuthToken?: () => Promise<string>;
}

// -----------------------------------------------------------------------------
// Adapter
// -----------------------------------------------------------------------------

export class NotesToHankAdapter {
  private ws: WebSocket | null = null;
  private readonly url: string;
  private readonly getAuthToken?: () => Promise<string>;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private isCleanedUp = false;

  // Callbacks consumed by the context
  onMessage: ((msg: NoteMessage) => void) | null = null;
  onError: ((error: Error) => void) | null = null;
  onConnectionChange: ((state: NoteConnectionState) => void) | null = null;

  constructor(config: NotesToHankAdapterConfig) {
    this.url = config.url;
    this.getAuthToken = config.getAuthToken;
  }

  async connect(): Promise<void> {
    if (this.isCleanedUp) {
      return;
    }

    this.emitConnectionChange('connecting');

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = async () => {
        LOG.info(`${LOG_PREFIX} WebSocket connected, sending auth`);

        // Server expects an authenticate message immediately after open
        if (this.getAuthToken) {
          try {
            const token = await this.getAuthToken();
            if (token && this.ws?.readyState === WebSocket.OPEN) {
              this.ws.send(JSON.stringify({ type: 'authenticate', token }));
              LOG.info(`${LOG_PREFIX} Auth message sent`);
            }
          } catch (err) {
            LOG.warn(`${LOG_PREFIX} Failed to get auth token:`, err);
          }
        }

        this.reconnectAttempts = 0;
        this.emitConnectionChange('connected');
        this.startPingInterval();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        const raw =
          typeof event.data === 'string' ? event.data : String(event.data);

        // Handle non-NoteMessage types from the server
        try {
          const parsed = JSON.parse(raw);

          // Server pong response (keepalive)
          if (parsed?.type === 'pong') {
            return;
          }

          // Server session-established acknowledgment
          if (parsed?.type === 'session-established') {
            LOG.info(
              `${LOG_PREFIX} Session established: ${parsed.session_id ?? ''}`
            );
            return;
          }

          // Server system message (e.g. shutdown notice)
          if (parsed?.type === 'system') {
            LOG.info(`${LOG_PREFIX} System message: ${parsed.message ?? ''}`);
            return;
          }
        } catch {
          // Not valid JSON, fall through to parseInboundMessage
        }

        const msg = parseInboundMessage(raw);
        if (msg && this.onMessage) {
          this.onMessage(msg);
        }
      };

      this.ws.onerror = () => {
        LOG.error(`${LOG_PREFIX} WebSocket error`);
        if (this.onError) {
          this.onError(new Error('WebSocket connection error'));
        }
      };

      this.ws.onclose = (event: CloseEvent) => {
        LOG.info(
          `${LOG_PREFIX} WebSocket closed: code=${event.code}, reason=${event.reason}`
        );
        if (!this.isCleanedUp) {
          this.attemptReconnect();
        }
      };
    } catch (err) {
      LOG.error(`${LOG_PREFIX} Failed to create WebSocket:`, err);
      this.emitConnectionChange('error');
      if (this.onError) {
        this.onError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }

  disconnect(): void {
    this.stopPingInterval();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close();
      }
      this.ws = null;
    }
    this.emitConnectionChange('disconnected');
  }

  sendMessage(msg: NoteMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      LOG.warn(`${LOG_PREFIX} Cannot send: WebSocket not open`);
      if (this.onError) {
        this.onError(new Error('WebSocket is not connected'));
      }
      return;
    }

    try {
      this.ws.send(JSON.stringify(msg));
    } catch (err) {
      LOG.error(`${LOG_PREFIX} Failed to send message:`, err);
      if (this.onError) {
        this.onError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }

  getConnectionState(): NoteConnectionState {
    if (!this.ws) {
      return 'disconnected';
    }
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      default:
        return 'disconnected';
    }
  }

  cleanup(): void {
    this.isCleanedUp = true;
    this.disconnect();
    this.onMessage = null;
    this.onError = null;
    this.onConnectionChange = null;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private emitConnectionChange(state: NoteConnectionState): void {
    if (this.onConnectionChange) {
      this.onConnectionChange(state);
    }
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // Ignore ping failures
        }
      }
    }, 30000);
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private attemptReconnect(): void {
    if (this.isCleanedUp) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      LOG.warn(`${LOG_PREFIX} Max reconnect attempts reached`);
      this.emitConnectionChange('error');
      return;
    }

    this.emitConnectionChange('reconnecting');
    this.reconnectAttempts++;

    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts - 1),
      30000
    );
    LOG.info(
      `${LOG_PREFIX} Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }
}
